import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  type AmbulanceDriverTypes,
  StatusAmbulanceDriver,
} from '@/domains/traffic-monitoring/constants/listEvent.constant';
import { cn } from '@/lib/utils';
import { useColorStore } from '@/store/colorStore';

// cm_language tags for StatusAmbulanceDriver - same tags AmbulanceCard.tsx
// uses, since the wording is the same fleet-status vocabulary either way.
const STATUS_TAGS: Record<AmbulanceDriverTypes, string> = {
  ongoing: 'psc>ambulance>on-going',
  standby: 'psc>ambulance>standby',
  maintenance: 'psc>ambulance>maintenance',
  offline: 'psc>ambulance>offline',
  requested: 'psc>ambulance>requested',
};

interface AmbulanceListProps {
  ambulanceDriverTypes: AmbulanceDriverTypes;
  no: number;
  plateNumber: string;
  hospital: string;
  pic: string;
  type: string;
  distance: string;
  nearest?: boolean;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}

const AmbulanceList = ({
  ambulanceDriverTypes,
  no,
  plateNumber,
  hospital,
  pic,
  type,
  distance,
  nearest = false,
  selected = false,
  disabled = false,
  onClick,
}: AmbulanceListProps) => {
  const { NEWCOLOR } = useColorStore();
  const { t } = useTranslation();
  const getStatusAmbulance = StatusAmbulanceDriver[ambulanceDriverTypes];
  const statusLabel = t(STATUS_TAGS[ambulanceDriverTypes]);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex flex-col lg:flex-row md:flex-row items-start lg:items-center justify-between w-full gap-5 rounded-lg border bg-db-border  bg-white dark:bg-bg-grey-dark p-4 shadow-sm cursor-pointer transition-all',
        selected && 'border-blue-500 ring-2 ring-blue-200',
        disabled && 'opacity-50 grayscale cursor-not-allowed'
      )}
    >
      <div className="w-[70%]  inline-flex  items-center justify-start gap-3 overflow-hidden text-ellipsis">
        <div className="">
          <span className="flex h-10 w-10  items-center justify-center rounded-full bg-text-blue-100 text-white h5bold">
            {disabled ? 'NA' : no}
          </span>
        </div>

        <div className=" flex-col gap-1 flex items-start text-center  justify-start">
          <span className="text-db-text dark:text-white   title1medium py-1 px-2 border rounded-[4px] dark:bg-[#F3F6FE]/10 bg-[#F3F6FE] ">
            {plateNumber}
          </span>
          <span className="text-left title2 whitespace-pre-wrap overflow-hidden text-ellipsis line-clamp-1">
            {hospital}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:gridd-cols-1 lg:grid-cols-6 md:grid-cols-6 gap-3 w-full items-center ">
        {/* PIC */}
        <div className="w-full grid col-span-2 gap-1 text-left pl-4">
          <span className="text-text-grey title2 uppercase">PIC</span>
          <span className="title2">{pic}</span>
        </div>

        {/* Type */}
        <div className="w-full grid col-span-2 gap-1 text-left border-l pl-4">
          <span className="text-text-grey title2 uppercase">
            {t('report>detail>ambulance type')}
          </span>
          <span className="title2">{type}</span>
        </div>

        {/* Distance */}
        <div className="w-full border-l pl-4   grid col-span-2">
          <p className="text-text-grey title2 uppercase text-left">
            {t('dispatch>master>distance to location')}
          </p>

          <div className="flex items-center gap-2">
            <span className="title1bold text-text-blue-100 ">{distance}</span>

            {nearest && (
              <span className="rounded-full border border-orange-400 px-2 py-0.5  title2 text-orange-500">
                NEAREST
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Status */}
      <div className="ml-0 lg:ml-auto md:ml-auto flex items-center gap-2">
        <span
          className="h-2 w-2 rounded-full"
          style={{ backgroundColor: NEWCOLOR[getStatusAmbulance.color] }}
        />

        <span className="title2 uppercase" style={{ color: NEWCOLOR[getStatusAmbulance.color] }}>
          {statusLabel}
        </span>
      </div>

      {/* Radio */}
      <div className="ml-2">
        <div
          className={cn(
            'flex h-5 w-5 items-center justify-center rounded-full border ',
            selected && 'border-text-blue-100'
          )}
        >
          {selected && <div className="h-2.5 w-2.5 rounded-full bg-text-blue-100" />}
        </div>
      </div>
    </button>
  );
};

export default AmbulanceList;
